// Small DOM helpers shared by the shell and the screens: build elements, hover tips, a toast and
// the API call. The dialog layer is in app/ui/modal.ts. Looks come from docs/design/mockup.html (app/styles.css).

import { APP_HEADER, BACKGROUND_HEADER } from "../../server/rules/sign-in";
import {
  COULD_NOT_LOAD,
  COULD_NOT_SAVE,
  isWrite,
  REQUEST_KEY_HEADER,
  SAVING_AGAIN,
  shouldRetry,
  WAIT_MS,
} from "../../server/rules/requests";

/** Builds an element: el("p", { class: "muted" }, "text", child). */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | boolean | undefined> = {},
  ...children: (Node | string | null | undefined | false)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === false) continue;
    node.setAttribute(k, v === true ? "" : v);
  }
  for (const c of children) if (c !== null && c !== undefined && c !== false) node.append(c);
  return node;
}

// ---- Hover tips, as in the mockup: any [data-tip] shows its text on hover or focus ----------

let tipEl: HTMLElement | null = null;
function tipShow(target: HTMLElement) {
  const text = target.dataset.tip;
  if (!text) return;
  tipEl ??= document.body.appendChild(el("div", { id: "tip", role: "tooltip" }));
  tipEl.textContent = text;
  tipEl.hidden = false;
  const r = target.getBoundingClientRect();
  const w = tipEl.offsetWidth;
  const x = Math.max(8, Math.min(r.left + r.width / 2 - w / 2, window.innerWidth - w - 8));
  let y = r.bottom + 8;
  if (y + 40 > window.innerHeight) y = r.top - tipEl.offsetHeight - 8;
  // The tip sits outside any zoom the text size puts on the page, so undo it for the position.
  const zoom = Number(getComputedStyle(document.documentElement).zoom) || 1;
  tipEl.style.left = `${x / zoom}px`;
  tipEl.style.top = `${y / zoom}px`;
}
/**
 * A file's name in a chip: ends in "…" when it doesn't fit, with the full name on hover (and in
 * the viewer), so a long name never pushes the chip's icons off a phone screen (brief 1D-6).
 */
export const fileName = (name: string) => el("span", { class: "fname", title: name }, name);

export const tipHide = () => {
  if (tipEl) tipEl.hidden = true;
};

export function startTips() {
  for (const type of ["mouseover", "focusin"] as const) {
    document.addEventListener(type, (e) => {
      const target = (e.target as Element | null)?.closest?.<HTMLElement>("[data-tip]");
      if (target) tipShow(target);
      else tipHide();
    });
  }
  document.addEventListener("scroll", tipHide, true);
}

// ---- Toast ----------------------------------------------------------------------------------

let toastTimer: ReturnType<typeof setTimeout> | undefined;
/** A short message at the bottom of the screen, as in the mockup. */
export function toast(message: string, sub?: string) {
  let t = document.getElementById("toast");
  if (!t) t = document.body.appendChild(el("div", { id: "toast", class: "toast", role: "status" }));
  t.replaceChildren(message, ...(sub ? [" ", el("small", {}, sub)] : []));
  t.classList.add("show");
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => t.classList.remove("show"), 2600);
}

/**
 * What every request tells the server (brief 1E-19c): the app it comes from, so the field app's
 * idle minutes apply; and, for what the app asks on its own (keeping a page current), that it
 * isn't the person doing something.
 */
export function requestHeaders(background = false): Record<string, string> {
  const field = typeof document !== "undefined" && document.body?.classList.contains("in-field");
  return {
    [APP_HEADER]: field ? "field" : "office",
    ...(background ? { [BACKGROUND_HEADER]: "1" } : {}),
  };
}

/**
 * What the unsaved changes helper (app/ui/unsaved.ts) hears from requests: how many are under
 * way, and each change the server took.
 */
export const requests = {
  open: 0,
  saved: [] as ((method: string) => void)[],
  /** Each save sent again after its answer was lost (brief 1E-27). */
  retried: [] as ((method: string, path: string) => void)[],
};

/** One try of a request: the answer, or status 0 when none came within the wait. */
async function tryOnce(
  method: string,
  path: string,
  headers: Record<string, string>,
  body: string | undefined,
  waitMs: number,
): Promise<{ status: number; json: boolean; parsed: unknown }> {
  const stop = new AbortController();
  const timer = setTimeout(() => stop.abort(), waitMs);
  try {
    const res = await fetch(path, {
      method,
      cache: "no-store",
      headers,
      body,
      signal: stop.signal,
    });
    const text = await res.text();
    let parsed: unknown = {};
    let json = false;
    try {
      parsed = text ? JSON.parse(text) : {};
      json = true;
    } catch {
      // Not the app's answer (an error page from between the browser and the app).
    }
    return { status: res.status, json, parsed };
  } catch {
    return { status: 0, json: false, parsed: {} };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Fetches JSON from the API: the one request helper every screen uses (brief 1E-27). Answers the
 * status and body and never throws. A try with no answer in time, or a lost answer, is sent once
 * more; a save is sent again with the same request key, so the server makes it only once and the
 * screen says so while it does. When that fails too, the body's error is "Couldn't load. Try
 * again." (or "Couldn't save").
 */
export async function api<T>(
  method: string,
  path: string,
  body?: unknown,
  o: { background?: boolean; app?: false } = {},
): Promise<{ ok: boolean; status: number; body: T & { error?: string } }> {
  const write = isWrite(method);
  // A screen's first view in one call (brief 1E-29): a plain read inside together() waits for it.
  if (gathering && !write && body === undefined && !o.background && o.app !== false)
    return gathered<T>(gathering, path);
  const sent = body === undefined ? undefined : JSON.stringify(body);
  // The same save pressed again after "Couldn't save" keeps its key: if a try did land and only
  // its answers were lost (both tries on an HTTP/2 connection that stopped answering), the server
  // sends that answer back instead of making the record twice.
  const same = `${method} ${path} ${sent ?? ""}`;
  const key = write ? (unanswered.get(same) ?? newRequestKey()) : null;
  const headers: Record<string, string> = {
    // app: false leaves the app the session was last used in as it is (the page's first /api/me).
    ...(o.app === false ? {} : requestHeaders(o.background)),
    ...(body === undefined ? {} : { "content-type": "application/json" }),
    ...(key ? { [REQUEST_KEY_HEADER]: key } : {}),
  };
  const waitMs = write ? WAIT_MS.write : WAIT_MS.read;
  requests.open++;
  try {
    let got = await tryOnce(method, path, headers, sent, waitMs);
    if (shouldRetry(got)) {
      if (write && !o.background) {
        for (const f of requests.retried) f(method, path);
        if (typeof document !== "undefined") toast(SAVING_AGAIN);
      }
      got = await tryOnce(method, path, headers, sent, waitMs);
    }
    if (shouldRetry(got)) {
      if (key) keepUnanswered(same, key);
      return {
        ok: false,
        status: got.status,
        body: { error: write ? COULD_NOT_SAVE : COULD_NOT_LOAD } as T & { error?: string },
      };
    }
    unanswered.delete(same);
    const ok = got.status >= 200 && got.status < 300;
    if (ok && write && !o.background) for (const f of requests.saved) f(method);
    return { ok, status: got.status, body: got.parsed as T & { error?: string } };
  } finally {
    requests.open--;
  }
}

/** Saves that got no answer on either try, by what was sent: their key, for pressing again. */
const unanswered = new Map<string, string>();
function keepUnanswered(same: string, key: string) {
  unanswered.delete(same);
  unanswered.set(same, key);
  // A handful is plenty; the oldest go first.
  for (const k of unanswered.keys()) {
    if (unanswered.size <= 20) break;
    unanswered.delete(k);
  }
}

/** A new request key for one save (sent again with the same key when its answer is lost). */
export function newRequestKey(): string {
  return crypto.randomUUID();
}

// ---- A screen's first view in one call (brief 1E-29) -------------------------------------------

type Answer = { ok: boolean; status: number; body: { error?: string } };
type Gathering = Map<string, ((a: Answer) => void)[]>;
let gathering: Gathering | null = null;

function gathered<T>(g: Gathering, path: string) {
  return new Promise<{ ok: boolean; status: number; body: T & { error?: string } }>((resolve) => {
    const waiting = g.get(path) ?? [];
    waiting.push(resolve as (a: Answer) => void);
    g.set(path, waiting);
  });
}

/**
 * Runs a screen's first draw with its reads asked in one call: every plain GET `fn` starts
 * before its first wait goes in one GET /api/first-view, the same address once, and each gets
 * the answer it would have had on its own (the route table checks each one). With one read, it
 * goes as usual.
 */
export async function together<R>(fn: () => Promise<R>): Promise<R> {
  const outer = gathering;
  const g: Gathering = outer ?? new Map();
  gathering = g;
  let run: Promise<R>;
  try {
    run = fn();
  } finally {
    gathering = outer;
  }
  if (!outer) void sendGathered(g);
  return run;
}

async function sendGathered(g: Gathering) {
  const paths = [...g.keys()];
  if (!paths.length) return;
  const settle = (path: string, a: Answer) => {
    // Each caller its own copy (two draws may each change what they got).
    for (const resolve of g.get(path) ?? []) resolve({ ...a, body: structuredClone(a.body) });
  };
  if (paths.length === 1) {
    const path = paths[0] as string;
    return settle(path, await api("GET", path));
  }
  const q = paths.map((p) => `get=${encodeURIComponent(p)}`).join("&");
  const res = await api<{ answers?: { status: number; body: unknown }[] }>(
    "GET",
    `/api/first-view?${q}`,
  );
  paths.forEach((path, i) => {
    const part = res.ok ? res.body.answers?.[i] : undefined;
    if (!part) return settle(path, { ok: false, status: res.status, body: res.body });
    settle(path, {
      ok: part.status >= 200 && part.status < 300,
      status: part.status,
      body: (part.body ?? {}) as { error?: string },
    });
  });
}
